import React, { useEffect, useMemo, useState } from "react";
import { getUser, removeCsrf, removeKey, removeUser } from "../../helpers";
import { useHistory, useLocation } from "react-router-dom";
import {
    FaTrash,
    FaCreditCard,
    FaUser,
    FaSignOutAlt,
    FaUserCircle,
    FaRobot,
    FaPlus,
    FaSearch,
    FaRegCommentDots,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { createErrorAlert, deleteCsrf, deleteUser } from "../../redux/actions";
import { Logout } from "../../services";

const API_BASE = "https://aibackend.studycrowdai.us/v1";
const AUTH_TOKEN = "Bearer app-mwVnSr9CJyRFLgcfBn49dXMM";

function ChatSidebar({ credits, onProfileClick, onLogoutClick, refreshTrigger }) {
    const [sessions, setSessions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const user = JSON.parse(getUser());
    const params = new URLSearchParams(location.search);
    const userId = params.get("user_id") || params.get("userid");
    const currentSessionId = params.get("session_id");

    useEffect(() => {
        fetchAllSessions();
    }, [userId, refreshTrigger]); // ✅ re-fetch when ChatInterface creates a new session

    const fetchAllSessions = async () => {
        if (!user?.id) return;
        try {
            const res = await fetch(
                `${API_BASE}/conversations?user=${user.id}&limit=50`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: AUTH_TOKEN,
                    },
                }
            );
            const response = await res.json();
            setSessions(response?.data || []);
        } catch (e) {
            console.error("Error fetching sessions:", e);
        }
    };

    const handleSessionClick = (id) => {
        history.push(`/aitutor?user_id=${user?.id}&session_id=${id}`);
    };

    const handleDeleteConversation = async (e, conversationId) => {
        e.stopPropagation();
        try {
            await fetch(`${API_BASE}/conversations/${conversationId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
                body: JSON.stringify({ user: user?.id }),
            });
        } catch (e) {
            console.error("Delete error:", e);
        } finally {
            fetchAllSessions();
            // If deleting current session, redirect to new chat
            if (conversationId === currentSessionId) {
                history.push(`/aitutor?user_id=${user?.id}&new=1`);
            }
        }
    };

    // ✅ Fixed handleNewChat — navigates without creating a session (session created on first message)
    const handleNewChat = () => {
        history.push(`/aitutor?user_id=${user?.id}&new=1`);
    };

    const handleProfileRoute = () => {
        history.push("/profile");
    };

    const handleSubscriptionsRoute = () => {
        history.push("/aitutor/subscriptions");
    };
    const handleLogout = () => {
        Logout()
            .then(() => {
                removeCsrf();
                removeKey();
                removeUser();
                dispatch(deleteCsrf());
                dispatch(deleteUser());
            })
            .catch(() => dispatch(createErrorAlert(2, "Error", "Try again", 2)));

        localStorage.clear();
        sessionStorage.clear();
        window.location.assign(`${window.location.origin}/login`);
    };

    const formatChatDate = (dateStr) => {
        const date = new Date(typeof dateStr === "number" ? dateStr * 1000 : dateStr);
        const now = new Date();

        const isSameDay = (d1, d2) =>
            d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear();

        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);

        if (isSameDay(date, now)) return "Today";
        if (isSameDay(date, yesterday)) return "Yesterday";

        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    // ✅ Group sessions by date
    const groupedSessions = useMemo(() => {
        const grouped = sessions.reduce((acc, session) => {
            const date = formatChatDate(session.created_at);
            if (!acc[date]) acc[date] = [];
            acc[date].push(session);
            return acc;
        }, {});

        if (!searchTerm.trim()) return grouped;

        const lowerSearch = searchTerm.toLowerCase();
        const filtered = {};
        Object.entries(grouped).forEach(([date, list]) => {
            const matching = list.filter((s) =>
                (s.name || "").toLowerCase().includes(lowerSearch)
            );
            if (matching.length > 0) filtered[date] = matching;
        });
        return filtered;
    }, [sessions, searchTerm]);

    return (
        <div className="w-72 h-screen fixed top-0 left-0 z-30 bg-primary text-white flex flex-col">

            {/* Account Button */}
            <div className="p-4 pb-0">
                <button
                    onClick={handleProfileRoute}
                    className="w-full py-2.5 px-4 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-20 rounded-lg text-white flex items-center justify-center gap-2 transition-colors duration-150"
                >
                    <FaUserCircle size={15} aria-hidden="true" />
                    Go to My Account
                </button>
            </div>

            {/* Header */}
            <div className="p-4 border-b border-white border-opacity-20">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                        <FaRobot size={14} aria-hidden="true" />
                    </div>
                    <span className="font-bold">Clara AI tutor</span>
                </div>

                <button
                    onClick={handleNewChat}
                    className="py-2.5 w-full bg-white bg-opacity-10 border border-white border-opacity-20 text-white rounded-lg hover:bg-opacity-20 flex items-center justify-center gap-2 transition-colors duration-150"
                >
                    <FaPlus size={12} aria-hidden="true" />
                    New Chat
                </button>

                <div className="relative mt-4">
                    <FaSearch
                        size={12}
                        className="transform absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                    />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search conversations..."
                        className="pl-8 pr-3 py-2 w-full bg-white border border-white border-opacity-20 text-black placeholder-gray-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-40"
                    />
                </div>
            </div>

            {/* Session list */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
                <h3 className="text-xs font-bold text-white text-opacity-60 uppercase tracking-wide mb-3">
                    Chat History
                </h3>

                {Object.entries(groupedSessions).map(([date, list]) => (
                    <div key={date} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-white text-opacity-50 uppercase tracking-wide whitespace-nowrap">
                                {date}
                            </span>
                            <div className="flex-1 h-px bg-white bg-opacity-10" />
                        </div>
                        {list.map((item) => {
                            const isActive = item.id === currentSessionId;
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleSessionClick(item.id)}
                                    className={`group relative flex items-center gap-2 pl-4 pr-2 py-2.5 rounded-lg cursor-pointer mb-1 transition-colors duration-150 ${
                                        isActive
                                            ? "bg-white bg-opacity-20"
                                            : "bg-white bg-opacity-0 hover:bg-opacity-10"
                                    }`}
                                >
                                    {isActive && (
                                        <span
                                            className="transform absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-white rounded-r"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <FaRegCommentDots
                                        size={13}
                                        className="text-white text-opacity-50 flex-shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span className="truncate text-sm text-white flex-1">{item.name}</span>
                                    <button
                                        onClick={(e) => handleDeleteConversation(e, item.id)}
                                        aria-label="Delete conversation"
                                        className="text-white text-opacity-50 hover:text-red-400 transition-colors duration-150 flex-shrink-0 opacity-0 group-hover:opacity-100"
                                    >
                                        <FaTrash size={12} aria-hidden="true" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ))}

                {Object.keys(groupedSessions).length === 0 && (
                    <div className="flex flex-col items-center text-center mt-8 px-4">
                        <FaRegCommentDots size={22} className="text-white text-opacity-30 mb-2" aria-hidden="true" />
                        <p className="text-sm text-white text-opacity-40">No conversations found</p>
                    </div>
                )}
            </div>

            {/* Bottom actions */}
            <div className="p-4 border-t border-white border-opacity-20 space-y-2">
                <button
                    onClick={handleSubscriptionsRoute}
                    className="w-full text-left text-white bg-white bg-opacity-0 hover:bg-opacity-10 p-2 rounded flex items-center gap-2 transition-colors duration-150"
                >
                    <FaCreditCard size={14} className="text-white text-opacity-70" aria-hidden="true" />
                    Subscriptions
                </button>
                <button
                    onClick={handleProfileRoute}
                    className="w-full text-left text-white bg-white bg-opacity-0 hover:bg-opacity-10 p-2 rounded flex items-center gap-2 transition-colors duration-150"
                >
                    <FaUser size={14} className="text-white text-opacity-70" aria-hidden="true" />
                    Profile
                </button>
                <button
                    onClick={handleLogout}
                    className="w-full text-left text-white bg-white bg-opacity-0 hover:bg-opacity-10 p-2 rounded flex items-center gap-2 transition-colors duration-150"
                >
                    <FaSignOutAlt size={14} className="text-white text-opacity-70" aria-hidden="true" />
                    Logout
                </button>
            </div>
        </div>
    );
}

export default ChatSidebar;
